/**
 * PerformanceSection — the "Performance" settings tab, shared by the Synth VR
 * and Flow settings dialogs (both mount this one component).
 *
 * Lets the user edit every knob of every perf-pill preset (Potato / Perf /
 * Auto / Quality): draw distance, render resolution, fps cap, dpr cap,
 * antialiasing and the Flow effect gates. Values live in the shared
 * editorStore's performanceSettings, so an edit here applies LIVE to both
 * editors and persists (localStorage + per-user server prefs).
 *
 * Each preset has its own Reset restoring the shipped defaults
 * (DEFAULT_PERFORMANCE — the exact values the presets hardcoded before they
 * became editable).
 */

import React, { useState } from 'react';
import { useEditorStore, DEFAULT_PERFORMANCE } from '../../store/editorStore';
import type { PerfMode, PerfPresetConfig } from '../../store/editorStore';
import SliderRow from '../ui/SliderRow';
import { segmentBtn, color, font, gradient } from '../ui/theme';
import {
  sectionStyle, hintTextStyle, resetBtnStyle, accordionBtnStyle,
  toggleRowStyle, toggleTrackStyle, toggleThumbStyle, fieldLabelStyle,
} from './SettingsDialog.styles';

const PRESETS: { key: PerfMode; label: string; blurb: string }[] = [
  { key: 'potato', label: 'Potato', blurb: 'For weak machines (integrated graphics).' },
  { key: 'performance', label: 'Perf', blurb: 'Short draw distance + lower resolution for max framerate.' },
  { key: 'auto', label: 'Auto', blurb: 'Adaptive: lowers density/resolution only when FPS drops.' },
  { key: 'quality', label: 'Quality', blurb: 'Full density and native resolution.' },
];

// The adaptive ladder's rungs — offered as the resolution choices so a pin and
// the ladder always land on the same values.
const SCALE_OPTIONS = [0.45, 0.55, 0.7, 0.85, 1];
const FPS_CAP_OPTIONS = [0, 30, 45, 60, 90, 120];
const DPR_CAP_OPTIONS: (number | null)[] = [null, 1, 1.5, 2];

const FX_ROWS: { key: keyof PerfPresetConfig['flowFx']; label: string }[] = [
  { key: 'sparks', label: 'Hit spark particles' },
  { key: 'waveformSprite', label: 'Runway waveform' },
  { key: 'railSweep', label: 'Trail sweep shimmer' },
  { key: 'beatChipsAllBeats', label: 'Number every beat (not just measures)' },
  { key: 'minimapLiveDuringPlay', label: 'Minimap updates during playback' },
];

export default function PerformanceSection() {
  const perfMode = useEditorStore(s => s.perfMode);
  const performanceSettings = useEditorStore(s => s.performanceSettings);
  const setPreset = useEditorStore(s => s.setPerformancePreset);
  const resetPreset = useEditorStore(s => s.resetPerformancePreset);
  const resetAll = useEditorStore(s => s.resetPerformanceSettings);
  const openBenchmark = useEditorStore(s => s.setBenchmarkOpen);
  // The ACTIVE preset starts expanded — it's the one whose edits show live.
  const [expanded, setExpanded] = useState<PerfMode>(perfMode);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={hintTextStyle}>
        Tune each performance preset (the pill above the 3D view). Changes to the
        <strong> active</strong> preset apply immediately, in both editors, and are
        saved to your account.
      </p>

      {/* Auto-tune: measures this machine and proposes values per preset. */}
      <button style={benchBtnStyle} onClick={() => openBenchmark(true)}>
        ⚡ Measure this machine & suggest values…
      </button>

      {PRESETS.map(({ key, label, blurb }) => {
        const cfg = performanceSettings[key];
        const isActive = key === perfMode;
        const isOpen = expanded === key;
        const patch = (p: Partial<PerfPresetConfig>) => setPreset(key, p);
        return (
          <div key={key} style={sectionStyle}>
            <button
              style={{ ...accordionBtnStyle, display: 'flex', alignItems: 'center', gap: 8 }}
              onClick={() => setExpanded(isOpen && !isActive ? perfMode : key)}
            >
              <span>{isOpen ? '▾' : '▸'} {label}</span>
              {isActive && <span style={activeChip}>active</span>}
              <span style={{ ...hintTextStyle, margin: 0, flex: 1, textAlign: 'right' }}>{blurb}</span>
            </button>

            {isOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 8 }}>
                {/* ── Draw distance ── */}
                <ToggleLine
                  label="Adaptive draw distance"
                  hint="The governor tunes how far ahead objects render, by framerate."
                  value={cfg.drawDistanceBeats == null}
                  onChange={v => patch({ drawDistanceBeats: v ? null : (DEFAULT_PERFORMANCE[key].drawDistanceBeats ?? 16) })}
                />
                {cfg.drawDistanceBeats != null && (
                  <SliderRow
                    label="Draw distance (beats)"
                    value={cfg.drawDistanceBeats}
                    min={8} max={50} step={0.5}
                    format={v => v.toFixed(1)}
                    onChange={v => patch({ drawDistanceBeats: v })}
                  />
                )}

                {/* ── Render resolution ── */}
                <div style={fieldLabelStyle}>Render resolution</div>
                <div style={segRow}>
                  <button
                    style={segmentBtn(cfg.renderScale == null)}
                    title="The adaptive ladder picks the sharpest setting your machine holds the target framerate at."
                    onClick={() => patch({ renderScale: null })}
                  >
                    Adaptive
                  </button>
                  {SCALE_OPTIONS.map(v => (
                    <button key={v} style={segmentBtn(cfg.renderScale === v)} onClick={() => patch({ renderScale: v })}>
                      {Math.round(v * 100)}%
                    </button>
                  ))}
                </div>
                {cfg.renderScale == null && (
                  <>
                    <div style={fieldLabelStyle}>Ladder starting point</div>
                    <div style={segRow}>
                      <button
                        style={segmentBtn(cfg.ladderSeed == null)}
                        title="Keep whatever the ladder was at (no forced start)."
                        onClick={() => patch({ ladderSeed: null })}
                      >
                        Keep
                      </button>
                      {SCALE_OPTIONS.map(v => (
                        <button key={v} style={segmentBtn(cfg.ladderSeed === v)} onClick={() => patch({ ladderSeed: v })}>
                          {Math.round(v * 100)}%
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {/* ── FPS cap ── */}
                <div style={fieldLabelStyle}>Frame-rate cap</div>
                <div style={segRow}>
                  {FPS_CAP_OPTIONS.map(v => (
                    <button key={v} style={segmentBtn(cfg.fpsCap === v)} onClick={() => patch({ fpsCap: v })}>
                      {v === 0 ? 'Off' : v}
                    </button>
                  ))}
                </div>
                <p style={hintTextStyle}>
                  A stable cap frees GPU for sharpness. If the machine can't hold the cap,
                  the tuner still thins note density to chase it.
                </p>

                {/* ── DPR cap ── */}
                <div style={fieldLabelStyle}>Pixel-ratio cap</div>
                <div style={segRow}>
                  {DPR_CAP_OPTIONS.map(v => (
                    <button
                      key={String(v)}
                      style={segmentBtn(cfg.dprCap === v)}
                      title={v == null ? 'No cap — the editor picks by render mode.' : `Render at most ${v}× CSS pixels before the render scale.`}
                      onClick={() => patch({ dprCap: v })}
                    >
                      {v == null ? 'Off' : `${v}×`}
                    </button>
                  ))}
                </div>

                {/* ── Antialias ── */}
                <ToggleLine
                  label="Antialiasing (MSAA)"
                  hint="Changing this rebuilds the 3D view — one brief flash. Off is the biggest single win on integrated GPUs."
                  value={cfg.antialias}
                  onChange={v => patch({ antialias: v })}
                />

                {/* ── Flow effects ── */}
                <div style={{ ...fieldLabelStyle, marginTop: 4 }}>Flow editor effects</div>
                {FX_ROWS.map(({ key: fxKey, label: fxLabel }) => (
                  <ToggleLine
                    key={fxKey}
                    label={fxLabel}
                    value={cfg.flowFx[fxKey]}
                    onChange={v => patch({ flowFx: { ...cfg.flowFx, [fxKey]: v } })}
                  />
                ))}

                <button style={{ ...resetBtnStyle, marginTop: 8 }} onClick={() => resetPreset(key)}>
                  ↺ Reset {label} to defaults
                </button>
              </div>
            )}
          </div>
        );
      })}

      <button style={resetBtnStyle} onClick={resetAll}>↺ Reset all presets</button>
    </div>
  );
}

/** Toggle row matching the dialogs' ToggleRow look (track + thumb + ON/OFF). */
function ToggleLine({ label, value, onChange, hint }: {
  label: string; value: boolean; onChange: (v: boolean) => void; hint?: string;
}) {
  return (
    <div>
      <div style={toggleRowStyle}>
        <span style={{ fontFamily: font.sans, fontSize: 12, color: color.ink, flex: 1 }}>{label}</span>
        <button
          onClick={() => onChange(!value)}
          style={{
            ...toggleTrackStyle,
            background: value
              ? gradient.brand
              : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
            borderColor: value
              ? 'oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.4)'
              : 'rgba(80, 60, 140, 0.3)',
          }}
        >
          <div style={{ ...toggleThumbStyle, transform: value ? 'translateX(14px)' : 'translateX(0)' }} />
        </button>
        <span style={{ fontSize: 9, color: color.ink, marginLeft: 4, fontFamily: font.sans }}>
          {value ? 'ON' : 'OFF'}
        </span>
      </div>
      {hint && <p style={{ ...hintTextStyle, marginTop: 2 }}>{hint}</p>}
    </div>
  );
}

const segRow: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 4 };

const benchBtnStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 10, cursor: 'pointer',
  fontFamily: font.sans, fontSize: 12, fontWeight: 700,
  background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.28), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.16))',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.45)',
  color: 'var(--accent)',
};

const activeChip: React.CSSProperties = {
  padding: '1px 8px', borderRadius: 999, fontSize: 9, fontWeight: 700,
  letterSpacing: 0.6, textTransform: 'uppercase',
  background: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.3)',
  color: 'var(--accent)', fontFamily: font.sans,
};
